import type {
  SpaceTransferExportResult,
  SpaceTransferImportResult,
  SpaceTransferReview,
} from "@rakazo/contracts";
import {
  SPACE_TRANSFER_LIMITS,
  SpaceTransferExportResultSchema,
  SpaceTransferImportResultSchema,
  SpaceTransferReviewSchema,
} from "@rakazo/contracts";
import { newClientId } from "./client-id";
import { withSpaceHeaders } from "./rpc";

export class SpaceTransferRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SpaceTransferRequestError";
  }
}

export async function spaceTransferAvailable(signal: AbortSignal): Promise<boolean> {
  const response = await fetch("/api/space-transfer/config", {
    credentials: "include",
    headers: withSpaceHeaders({}),
    signal,
  });
  if (!response.ok) return false;
  const value: unknown = await response.json();
  return Boolean(
    value && typeof value === "object" && "enabled" in value && value.enabled === true,
  );
}

async function checkedJson(response: Response): Promise<unknown> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      body && typeof body === "object" && "error" in body && typeof body.error === "string"
        ? body.error
        : "";
    throw new SpaceTransferRequestError(message);
  }
  return body;
}

export async function reviewSpaceTransfer(
  spaceId: string,
  signal: AbortSignal,
): Promise<SpaceTransferReview> {
  const response = await fetch("/api/space-transfer/review", {
    method: "POST",
    credentials: "include",
    headers: withSpaceHeaders({ "content-type": "application/json" }, spaceId),
    body: "{}",
    signal,
  });
  return SpaceTransferReviewSchema.parse(await checkedJson(response));
}

export async function prepareSpaceExport(
  spaceId: string,
  input: { password: string; reviewed: true; operationKey: string },
  signal: AbortSignal,
): Promise<SpaceTransferExportResult> {
  const response = await fetch("/api/space-transfer/export", {
    method: "POST",
    credentials: "include",
    headers: withSpaceHeaders({ "content-type": "application/json" }, spaceId),
    body: JSON.stringify(input),
    signal,
  });
  const result = SpaceTransferExportResultSchema.parse(await checkedJson(response));
  spaceTransferDownloadPath(result.downloadUrl);
  return result;
}

/** Only an owned download route can become a browser navigation. No credentials in its URL.
 * Лише маршрут власного архіву може стати адресою завантаження. Пароля в адресі немає. */
export function spaceTransferDownloadPath(value: string): string {
  const url = new URL(value, window.location.origin);
  if (
    url.origin !== window.location.origin ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    !/^\/api\/space-transfer\/download\/[A-Za-z0-9_-]{1,96}$/.test(url.pathname)
  )
    throw new SpaceTransferRequestError("Invalid archive download address");
  return url.pathname;
}

export function downloadSpaceArchive(result: SpaceTransferExportResult): void {
  const link = document.createElement("a");
  link.href = spaceTransferDownloadPath(result.downloadUrl);
  link.download = result.filename;
  document.body.append(link);
  link.click();
  link.remove();
}

// A lost reply must retry the same import. Passwords never enter this map or storage.
// Після втрати відповіді повторюємо той самий імпорт. Паролі не зберігаються тут чи на диску.
const importKeys = new Map<string, string>();
const IMPORT_RETRIES_STORAGE_KEY = "hrokbot:space-transfer-retries";

function rememberedImportKey(fingerprint: string): string | undefined {
  const known = importKeys.get(fingerprint);
  if (known) return known;
  try {
    const saved: unknown = JSON.parse(sessionStorage.getItem(IMPORT_RETRIES_STORAGE_KEY) ?? "[]");
    if (!Array.isArray(saved)) return;
    const record = saved.find((item) => Array.isArray(item) && item[0] === fingerprint);
    if (record && typeof record[1] === "string" && /^[A-Za-z0-9_-]{16,64}$/.test(record[1]))
      return record[1];
  } catch {
    // Session storage can be unavailable. An in-page retry still retains its key.
    // Сховище сеансу може бути недоступним; повтор на відкритій сторінці зберігає ключ.
  }
}

function rememberImportKey(fingerprint: string, key?: string): void {
  if (key) importKeys.set(fingerprint, key);
  else importKeys.delete(fingerprint);
  try {
    const saved: unknown = JSON.parse(sessionStorage.getItem(IMPORT_RETRIES_STORAGE_KEY) ?? "[]");
    const records = Array.isArray(saved)
      ? saved.filter((item) => Array.isArray(item) && item[0] !== fingerprint)
      : [];
    if (key) records.push([fingerprint, key]);
    sessionStorage.setItem(IMPORT_RETRIES_STORAGE_KEY, JSON.stringify(records));
  } catch {
    // The request does not depend on optional browser storage.
    // Запит не залежить від необов’язкового сховища браузера.
  }
}
function importFingerprint(file: File, name: string): string {
  return JSON.stringify([file.name, file.size, file.lastModified, name.trim()]);
}

export async function importSpaceArchive(
  file: File,
  name: string,
  password: string,
  signal: AbortSignal,
): Promise<SpaceTransferImportResult> {
  if (file.size > SPACE_TRANSFER_LIMITS.maxCompressedBytes)
    throw new SpaceTransferRequestError("The archive is larger than the allowed size.");
  const fingerprint = importFingerprint(file, name);
  let key = rememberedImportKey(fingerprint);
  if (!key) {
    key = newClientId();
    rememberImportKey(fingerprint, key);
  }
  const response = await fetch("/api/space-transfer/import", {
    method: "POST",
    credentials: "include",
    headers: withSpaceHeaders({
      "content-type": "application/vnd.hrokbot.space-bundle",
      "x-hrokbot-transfer-password": encodeURIComponent(password),
      "x-hrokbot-space-name": encodeURIComponent(name.trim()),
      "x-hrokbot-transfer-key": key,
    }),
    body: file,
    signal,
  });
  const result = SpaceTransferImportResultSchema.parse(await checkedJson(response));
  rememberImportKey(fingerprint);
  return result;
}
